import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styled from "styled-components";
import { FiX, FiGithub, FiArrowUpRight } from "react-icons/fi";
import MonogramTile from "../Visual/MonogramTile";
import { Bio } from "../../data/constants";

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 1300;
  background: rgba(3, 3, 6, 0.75);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
  padding: 60px 16px;
`;

const Panel = styled(motion.div)`
  width: 100%;
  max-width: 720px;
  border-radius: 20px;
  background: #0a0a0f;
  border: 1px solid rgba(255, 255, 255, 0.09);
  overflow: hidden;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(5, 5, 7, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #fff;
  cursor: pointer;
  backdrop-filter: blur(6px);

  &:hover {
    border-color: ${({ theme }) => theme.primary};
  }
`;

const VisualWrap = styled.div`
  height: 220px;
`;

const Body = styled.div`
  padding: 30px 32px 32px;

  @media (max-width: 600px) {
    padding: 24px 20px 26px;
  }
`;

const Title = styled.h2`
  font-family: "Space Grotesk", sans-serif;
  font-size: clamp(1.4rem, 3vw, 1.8rem);
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
`;

const Meta = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.text_muted};
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 18px 0 6px;
`;

const Tag = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.secondary};
  background: rgba(34, 211, 238, 0.1);
  border: 1px solid rgba(34, 211, 238, 0.2);
  padding: 4px 11px;
  border-radius: 100px;
`;

const Desc = styled.p`
  margin-top: 14px;
  font-size: 15px;
  line-height: 1.8;
  color: ${({ theme }) => theme.text_secondary};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 28px;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 22px;
  border-radius: 100px;
  background: ${({ theme }) => theme.gradient};
  color: #050507;
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;
  transition: transform 0.25s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const GhostButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 22px;
  border-radius: 100px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: ${({ theme }) => theme.text_primary};
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;
  transition: all 0.25s ease;

  &:hover {
    border-color: ${({ theme }) => theme.primary};
    background: rgba(139, 124, 255, 0.08);
  }
`;

const Note = styled.p`
  margin-top: 26px;
  font-size: 13px;
  color: ${({ theme }) => theme.text_muted};
`;

const ProjectDetails = ({ openModal, setOpenModal }) => {
  const project = openModal?.project;
  const close = () => setOpenModal({ state: false, project: null });

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!project) return null;

  const hasLiveApp = project.webapp && project.webapp !== Bio.github;
  const hasCode = project.github && project.github !== Bio.github;

  return (
    <AnimatePresence>
      <Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={close}
      >
        <Panel
          initial={{ opacity: 0, y: 24, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.98 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton onClick={close} aria-label="Close">
            <FiX />
          </CloseButton>
          <VisualWrap>
            <MonogramTile label={project.title} seed={project.title} minHeight="100%" fontSize="clamp(2rem, 5vw, 3.2rem)" />
          </VisualWrap>
          <Body>
            <Title>{project.title}</Title>
            <Meta>{project.date}</Meta>
            <Tags>
              {project.tags?.map((tag) => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </Tags>
            <Desc>{project.description}</Desc>
            {(hasLiveApp || hasCode) && (
              <ButtonGroup>
                {hasLiveApp && (
                  <PrimaryButton href={project.webapp} target="_blank" rel="noopener noreferrer">
                    Visit Live Site <FiArrowUpRight />
                  </PrimaryButton>
                )}
                {hasCode && (
                  <GhostButton href={project.github} target="_blank" rel="noopener noreferrer">
                    <FiGithub /> Source Code
                  </GhostButton>
                )}
              </ButtonGroup>
            )}
            {!hasLiveApp && !hasCode && <Note>This project isn't public.</Note>}
          </Body>
        </Panel>
      </Overlay>
    </AnimatePresence>
  );
};

export default ProjectDetails;
